import type { GetState, SetState } from "zustand";

import type { ResponseImprovement, ResponseImprovementState } from "../../types";
import type { AskPerformYardState } from "../types";

import { applyReviewAnswer } from "../../utils/applyReviewAnswer";

import { persistSuggestionOutcome } from "../helpers";

/**
 * Form Authoring Coach slice.
 *
 * Carries the popup CTA's hand-off — the callout can't send the evaluation itself (opening the panel
 * unmounts it mid-request), so it parks a flag the panel picks up once mounted, the same hand-off the
 * goal coach uses — the report id every request forwards, and the per-suggestion state machine behind
 * the improvement cards.
 */
export const createFormAuthoringCoachSlice = (
	set: SetState<AskPerformYardState>,
	get: GetState<AskPerformYardState>
) => ({
	pendingFormAuthoringCoachStart: false,
	authoringReviewReportId: null as string | null,
	responseImprovementStates: [] as ResponseImprovementState[],
	responseImprovementError: null as { messageId: string; message: string } | null,
	aiImprovedAnswerIndexes: [] as number[],

	setPendingFormAuthoringCoachStart: (value: boolean) => set({ pendingFormAuthoringCoachStart: value }),

	setAuthoringReviewReportId: (value: string | null) => set({ authoringReviewReportId: value }),

	// Read-and-clear against the live store so the send fires once even under StrictMode's double-mount.
	consumePendingFormAuthoringCoachStart: (): boolean => {
		let wasPending = false;
		set((state) => {
			wasPending = state.pendingFormAuthoringCoachStart;
			return { pendingFormAuthoringCoachStart: false };
		});
		return wasPending;
	},

	setResponseImprovements: (messageId: string, suggestions: ResponseImprovement[]) => {
		set((state) => {
			const msgIdx = state.messages.findIndex((m) => m.id === messageId);
			const updatedMessages = state.messages.slice();
			if (msgIdx !== -1) {
				updatedMessages[msgIdx] = { ...updatedMessages[msgIdx], responseImprovements: suggestions };
			}

			const newStates: ResponseImprovementState[] = suggestions.map((s) => ({
				suggestion: s,
				status: "pending" as const,
				messageId,
			}));

			// Replace any existing states for this messageId; keep states from other messages.
			const otherStates = state.responseImprovementStates.filter((rs) => rs.messageId !== messageId);

			return {
				messages: updatedMessages,
				responseImprovementStates: [...otherStates, ...newStates],
				// A fresh set of cards makes the last failure stale.
				responseImprovementError: null,
			};
		});
	},

	// Guard + write + mark in one action: only a pending suggestion applies, and only when its answer
	// field is actually on the page — otherwise the card would read "Change Applied" over a no-op.
	applyResponseImprovement: (suggestionId: string) => {
		const target = get().responseImprovementStates.find((rs) => rs.suggestion.id === suggestionId);
		// A restored card targets the answer as it was on the review that was open at the
		// time — writing it now could land on a different report's field. The UI already
		// hides the button; this is the layer under it.
		if (!target || target.status !== "pending" || target.isRestored) return;
		const { questionIndex, questionType, suggestedAnswer } = target.suggestion;
		if (!applyReviewAnswer(questionIndex, questionType, suggestedAnswer)) {
			// The card outlived the answer it targets — the author navigated off the review, or the
			// field is no longer editable. Say so, but in the coach's own slot: the thread-level
			// `error` doubles as the stream's status, so writing there mid-turn would cancel the
			// answer being generated and offer to re-run it.
			//
			// Carries the message it belongs to, the way the card states already do: run the coach twice
			// in one thread and both sets of cards stay on screen, so an unscoped string would banner
			// them both and let a dismiss on either clear the other.
			set({
				responseImprovementError: {
					messageId: target.messageId,
					message:
						"That answer isn't on the page anymore, so the change couldn't be applied. Open the review you were filling out and try again.",
				},
			});
			return;
		}
		// Durable, so a restored card can still show "Change Applied" after a reload (helpers.ts).
		persistSuggestionOutcome(suggestionId, { kind: "responseImprovement" });
		// Marked after the write: applying dispatches the page's own input/click events, and the
		// "author started typing" listener that clears this flag reads them.
		set((state) => ({
			responseImprovementStates: state.responseImprovementStates.map((rs) =>
				rs.suggestion.id === suggestionId ? { ...rs, status: "applied" as const } : rs
			),
			responseImprovementError: null,
			aiImprovedAnswerIndexes: state.aiImprovedAnswerIndexes.includes(questionIndex)
				? state.aiImprovedAnswerIndexes
				: [...state.aiImprovedAnswerIndexes, questionIndex],
		}));
	},

	dismissResponseImprovementError: () => set({ responseImprovementError: null }),

	/** Drops the "Improved with AI" label once the author edits that answer themselves. */
	clearAnswerImprovedWithAi: (questionIndex: number) => {
		set((state) =>
			state.aiImprovedAnswerIndexes.includes(questionIndex)
				? {
						aiImprovedAnswerIndexes: state.aiImprovedAnswerIndexes.filter(
							(index) => index !== questionIndex
						),
					}
				: state
		);
	},

	markResponseImprovementDeleted: (suggestionId: string) => {
		set((state) => ({
			responseImprovementStates: state.responseImprovementStates.map((rs) =>
				rs.suggestion.id === suggestionId ? { ...rs, status: "deleted" as const } : rs
			),
		}));
	},
});
